import { useState, useEffect } from "react";
import { ChevronUp, Mail, ExternalLink, HelpCircle, ChevronDown } from "lucide-react";

const faqs = [
  {
    id: "start-game",
    question: "How do I start a game?",
    answer: (
      <p>
        Tap "New Game" from the home screen. Enter a course name (or search for one), pick
        your game type (Skins, Nassau, Wolf, Best Ball, or Both), add your players, and tap
        "Start Game."
      </p>
    ),
  },
  {
    id: "live-scoring",
    question: "How does Live Scoring work?",
    answer: (
      <p>
        Live Scoring lets everyone in your foursome see scores update in real time on their
        own phone. The host creates the game, taps "Invite," and enables Live Scoring. Other
        players enter the 6-character game code to join. Live Scoring requires a Premium
        subscription.
      </p>
    ),
  },
  {
    id: "track-bets",
    question: "How do I track bets across multiple rounds?",
    answer: (
      <p>
        The Tab is a running ledger that tracks running balances across rounds with the same
        group. After a round, tap "Add to The Tab" to log the round's results. The Tab shows
        who's up and who's down over weeks or months. Free users get one Tab; Premium users
        can track unlimited Tabs.
      </p>
    ),
  },
  {
    id: "offline",
    question: "Does the app work offline?",
    answer: (
      <p>
        Yes. Golf Skins is offline-first. You can score a full round without signal and it
        will sync automatically when you're back in range.
      </p>
    ),
  },
  {
    id: "cancel-subscription",
    question: "How do I cancel my subscription?",
    answer: (
      <>
        <p>
          Subscriptions are managed through your Apple ID, not inside the app. To cancel:
        </p>
        <ol className="pp-ordered-list">
          <li>Open Settings on your iPhone</li>
          <li>Tap your name at the top</li>
          <li>Tap Subscriptions</li>
          <li>Tap Golf Skins and then Cancel Subscription</li>
        </ol>
      </>
    ),
  },
  {
    id: "delete-account",
    question: "How do I delete my account?",
    answer: (
      <p>
        Go to Settings inside the app, scroll to the Danger Zone section, and tap "Delete
        Account." This permanently removes your account and all associated data from our
        servers.
      </p>
    ),
  },
  {
    id: "data-handling",
    question: "How is my data handled?",
    answer: (
      <p>
        We only collect what's needed to run the app — your name, email (via Apple Sign In),
        game data, and anonymized usage analytics. We don't sell or share your data. Full
        details in our{" "}
        <a href="/golfskins/privacy" className="pp-link" data-testid="gs-faq-privacy-link">
          Privacy Policy
        </a>
        .
      </p>
    ),
  },
];

const FaqItem = ({ faq }) => {
  const [open, setOpen] = useState(false);

  return (
    <div className="sp-faq-item" data-testid={`gs-faq-item-${faq.id}`}>
      <button
        className={`sp-faq-question ${open ? "open" : ""}`}
        onClick={() => setOpen(!open)}
        data-testid={`gs-faq-toggle-${faq.id}`}
        aria-expanded={open}
      >
        <span>{faq.question}</span>
        <ChevronDown size={18} className={`sp-faq-chevron ${open ? "rotated" : ""}`} />
      </button>
      {open && (
        <div className="sp-faq-answer" data-testid={`gs-faq-answer-${faq.id}`}>
          {faq.answer}
        </div>
      )}
    </div>
  );
};

const GolfSkinsSupport = () => {
  const [showBackToTop, setShowBackToTop] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setShowBackToTop(window.scrollY > 400);
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className="pp-page" data-testid="golfskins-support-page">
      <main className="pp-main" data-testid="golfskins-support-content">
        <article className="pp-article">
          {/* Header */}
          <header className="pp-header" data-testid="golfskins-support-header">
            <div className="pp-header-icon pp-header-icon--support">
              <HelpCircle size={28} strokeWidth={1.5} />
            </div>
            <h1 className="pp-title">Golf Skins Support</h1>
            <p className="sp-subtitle">
              Need help with Golf Skins? You're in the right place.
            </p>
          </header>

          {/* Contact Us */}
          <section id="contact-us" className="pp-section" data-testid="section-gs-support-contact">
            <h2 className="pp-section-heading">Contact Us</h2>
            <div className="sp-email-card" data-testid="gs-support-email-card">
              <a
                href="mailto:support@golfskinsapp.com"
                className="sp-email-link"
                data-testid="gs-support-email-link"
              >
                <Mail size={20} />
                support@radships.com
              </a>
            </div>
            <p>
              We read every message and usually respond within 24–48 hours. Please include your
              device model and a description of what you were doing when the issue happened.
            </p>
          </section>

          {/* FAQs */}
          <section id="faq" className="pp-section" data-testid="section-gs-faqs">
            <h2 className="pp-section-heading">Frequently Asked Questions</h2>
            <div className="sp-faq-list" data-testid="gs-faq-list">
              {faqs.map((faq) => (
                <FaqItem key={faq.id} faq={faq} />
              ))}
            </div>
          </section>

          {/* Managing Your Subscription */}
          <section id="subscription" className="pp-section" data-testid="section-gs-subscription">
            <h2 className="pp-section-heading">Managing Your Subscription</h2>
            <p>Golf Skins offers two Premium subscription plans:</p>
            <ul className="pp-list">
              <li>
                <strong>Monthly:</strong> $7.99 per month, with a 7-day free trial
              </li>
              <li>
                <strong>Yearly:</strong> $47.99 per year, with a 14-day free trial (save 50%
                vs monthly)
              </li>
            </ul>
            <p>
              Premium unlocks Live Scoring, unlimited Tabs, and games with 5 or more players.
            </p>

            <p>
              <strong>Trial terms:</strong> Your free trial starts when you tap "Start Free
              Trial." You will not be charged during the trial period. If you do not cancel at
              least 24 hours before the trial ends, your subscription will renew at the regular
              price. You can cancel anytime from your iPhone Settings under Subscriptions.
            </p>

            <p>
              <strong>Refund requests:</strong> Refunds for Apple subscriptions are handled by
              Apple directly. You can request a refund at{" "}
              <a
                href="https://reportaproblem.apple.com"
                target="_blank"
                rel="noopener noreferrer"
                className="pp-link"
                data-testid="gs-refund-link"
              >
                reportaproblem.apple.com
                <ExternalLink size={12} className="pp-external-icon" />
              </a>
              .
            </p>
          </section>

          {/* Footer */}
          <footer className="pp-footer" data-testid="golfskins-support-footer">
            <div className="gs-footer-links" data-testid="gs-footer-links">
              <a href="/golfskins/privacy" data-testid="gs-footer-privacy-link">
                Privacy Policy
              </a>
            </div>
            <p>&copy; {new Date().getFullYear()} Golf Skins. All rights reserved.</p>
          </footer>
        </article>
      </main>

      {/* Back to top */}
      {showBackToTop && (
        <button
          onClick={scrollToTop}
          className="pp-back-to-top"
          aria-label="Back to top"
          data-testid="gs-support-back-to-top"
        >
          <ChevronUp size={20} />
        </button>
      )}
    </div>
  );
};

export default GolfSkinsSupport;
